import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { ScanLine, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ScanPresence() {
  const [status, setStatus] = useState("idle"); 
  const [result, setResult] = useState("");
  const [ready, setReady] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const qrRef = useRef(null);
  const lockRef = useRef(false);
  const navigate = useNavigate();

  
  const successSoundRef = useRef(null);
  const errorSoundRef = useRef(null);

  
  function extractMemberId(text) {
    try {
      if (text.startsWith("http")) {
        const url = new URL(text);
        return url.searchParams.get("mid");
      }
      return text;
    } catch {
      return null;
    }
  }

  
  function unlockAudio() {
    if (audioUnlocked) return;

    successSoundRef.current = new Audio("/sounds/success.mp3");
    errorSoundRef.current = new Audio("/sounds/error.mp3");

    successSoundRef.current
      .play()
      .then(() => {
        successSoundRef.current.pause();
        successSoundRef.current.currentTime = 0;
        setAudioUnlocked(true);
        initScanner();
      })
      .catch(() => {
        console.warn("Audio unlock failed");
        initScanner();
      });
  }

  
  function initScanner() {
    if (qrRef.current) return;

    const qr = new Html5Qrcode("reader");
    qrRef.current = qr;

    qr
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 230, height: 230 } },
        onScan
      )
      .then(() => setReady(true))
      .catch((err) => console.error("Camera error:", err));
  }

  
  async function onScan(decodedText) {
    if (lockRef.current) return;
    lockRef.current = true;

    const member_id = extractMemberId(decodedText);

    if (!member_id) {
      showError("QR code invalide");
      return reset();
    }

    setStatus("loading");
    setResult("Vérification en cours…");

    try {
      const token = localStorage.getItem("membreToken");

      if (!token) {
        navigate("/login");
        return;
      }


      const res = await fetch("/api/presence?action=scan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token
        },
        body: JSON.stringify({ member_id }),
      });


      if (res.status === 403) {
        localStorage.removeItem("membreToken");
        navigate("/login");
        return;
      }

      const json = await res.json();

      if (!json.success) {
        showError(
          json.message?.toLowerCase().includes("déjà")
            ? "Membre déjà enregistré"
            : json.message
        );
      } else {
        setStatus("success");
        setResult(`${json.user.prenom} ${json.user.nom}`);

        successSoundRef.current.currentTime = 0;
        successSoundRef.current.play();
        navigator.vibrate?.(200);
      }
    } catch {
      showError("Erreur serveur");
    }

    reset();
  }

  function showError(msg) {
    setStatus("error");
    setResult(msg);

    errorSoundRef.current.currentTime = 0;
    errorSoundRef.current.play();
  }

  function reset() {
    setTimeout(() => {
      setStatus("idle");
      setResult("");
      lockRef.current = false;
    }, 2200);
  }

  
  useEffect(() => {
    return () => {
      qrRef.current?.stop().catch(() => { });
    };
  }, []);

  
  return (
    <div className="h-screen overflow-hidden bg-gradient-to-b from-slate-950 to-blue-900 text-white flex flex-col">

      <header className="px-5 py-4 text-center border-b border-white/10 shrink-0">
        <h1 className="text-lg font-bold tracking-wide">
          Scanner de présence
        </h1>
        <p className="text-xs opacity-70">
          Présentez le QR code du membre
        </p>
      </header>

      {!ready && (
        <div className="flex justify-center mt-6">
          <button
            onClick={unlockAudio}
            className="px-6 py-3 bg-green-500 rounded-full text-white font-bold shadow-lg active:scale-95"
          >
            Démarrer le scan
          </button>
        </div>
      )}

      <div className="flex items-center justify-center px-5 pt-4 pb-2">
        <div className="relative w-64 sm:w-72">
          <div
            id="reader"
            className={`
              aspect-square rounded-3xl bg-black overflow-hidden
              shadow-2xl transition-all duration-300
              ${status === "success" ? "ring-8 ring-green-400 scale-105" : ""}
              ${status === "error" ? "ring-8 ring-red-400" : ""}
            `}
          />
          <div className="absolute inset-0 rounded-3xl pointer-events-none border-4 border-white/20" />
        </div>
      </div>

      <div className="px-6 pb-6 text-center h-[160px] flex flex-col justify-center">

        {!ready && (
          <div className="flex flex-col items-center gap-2 text-yellow-300">
            <Loader2 className="animate-spin" size={32} />
            <p>Initialisation caméra…</p>
          </div>
        )}

        {ready && status === "idle" && (
          <div className="flex flex-col items-center gap-2 opacity-80">
            <ScanLine size={34} />
            <p className="font-semibold">Scannez le QR code</p>
          </div>
        )}

        {status === "loading" && (
          <div className="flex flex-col items-center gap-2 text-yellow-300">
            <Loader2 size={32} className="animate-spin" />
            <p className="font-bold">{result}</p>
          </div>
        )}

        {status === "success" && (
          <div className="flex flex-col items-center gap-2 text-green-300">
            <CheckCircle2 size={36} />
            <p className="text-lg font-extrabold">Présence validée</p>
            <p>{result}</p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-2 text-red-300">
            <XCircle size={36} />
            <p className="text-lg font-extrabold">Erreur</p>
            <p>{result}</p>
          </div>
        )}

        <p className="mt-2 text-xs opacity-60">
          Scan suivant automatique
        </p>
      </div>
    </div>
  );
}
